import { useState } from "react";

import { SliceZone } from "@prismicio/react";
import _get from "lodash/get";
import { ParsedQs } from "qs";
import { FormProvider, useForm } from "react-hook-form";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import SecondaryButton from "components/button/secondaryButton/SecondaryButton";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import MultiLineText from "components/form/multiLineText/MultiLineText";
import SingleLineText from "components/form/singleLineText/SingleLineText";
import Modal from "components/modal/Modal";
import {
  useGetProduct,
  useGetSchema,
  useSetCustomisation,
} from "queries/product-queries";
import { useGetCustomisation } from "queries/product-queries";
import { components } from "slices/fields";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { useGetDefaultValuesFromFormDocuments } from "utils/form-utils";
import { useGetGlobalFormFromSchema } from "utils/schema-utils";

import { AdminToolComponentProps } from "../adminTool/AdminTool";

import styles from "./PersonalizationManager.module.scss";

export default function PersonalizationManager({
  description,
  isOpen,
  onDismiss,
  title,
}: AdminToolComponentProps) {
  const methods = useForm();

  const { data: schema } = useGetSchema();
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();
  const { mutate: setCustomisation } = useSetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  function onSubmit(data: ParsedQs) {
    setCustomisation({ data });
    onDismiss();
  }

  /**
   * Submit form with only schema entries marked as required
   */
  function onClearOptionalParams() {
    if (!schema || !customisation) {
      return;
    }

    const requiredParams: ParsedQs = {};

    Object.entries(customisation).forEach(([key, value]) => {
      const field = _get(schema.keys, key);

      if (field && "flags" in field && field.flags?.presence === "required") {
        requiredParams[key] = value;
      }
    });

    onSubmit(requiredParams);
  }

  /**
   * Submit form with data from
   */
  function onUseExampleValues() {
    if (!schema) {
      return;
    }

    const defaults: ParsedQs = {};

    Object.entries(schema.keys).forEach(([key, value]) => {
      if ("examples" in value && value.examples) {
        defaults[key] = value.examples[0];
      }
    });

    onSubmit(defaults);
  }

  if (!customisation) {
    return null;
  }

  return (
    <Modal
      className={styles.modal}
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={title}
    >
      <div className={styles.fullHeightContainer}>
        <div className={styles.fields}>
          <div>
            <p className={styles.title}>{title}</p>
            <p className={styles.subtitle}>{description}</p>
          </div>
          <div>
            <div className={styles.field}>
              <p className={styles.label}>Quick actions</p>

              <div className={styles.quickActions}>
                <SecondaryButton
                  color="green"
                  onClick={onClearOptionalParams}
                  type="button"
                >
                  Clear optional parameters
                </SecondaryButton>

                <SecondaryButton
                  color="green"
                  onClick={onUseExampleValues}
                  type="button"
                >
                  Use example values
                </SecondaryButton>
              </div>
            </div>
          </div>

          <hr className={styles.divider} />

          <div>
            <label className={styles.field}>
              <p className={styles.label}>Muse Product Slug Override</p>
              <p className={styles.sublabel}>
                Use this to point to a different product slug on Muse. Helpful
                when changes you need are in pre-release.
              </p>
              <form
                className={styles.param}
                onSubmit={methods.handleSubmit((data) =>
                  onSubmit({ ...customisation, ...data })
                )}
              >
                <SingleLineText
                  className={styles.input}
                  placeholder={`e.g. ${product.museSlug}-prerelease`}
                  defaultValue={
                    (customisation.muse_slug_override as string) || ""
                  }
                  {...methods.register("muse_slug_override")}
                />
                <PrimaryButton type="submit">Submit</PrimaryButton>
              </form>
            </label>
          </div>

          <hr className={styles.divider} />
        </div>

        <PersonalizationTabForms onSubmit={onSubmit} />
      </div>
    </Modal>
  );
}

type PersonalizationTabFormsProps = {
  onSubmit: (data: any) => void;
};

function PersonalizationTabForms({ onSubmit }: PersonalizationTabFormsProps) {
  const { data: customisation } = useGetCustomisation();

  const schemaForm = useGetGlobalFormFromSchema();

  const methods = useForm({
    defaultValues: useGetDefaultValuesFromFormDocuments([schemaForm]),
    shouldUnregister: true,
  });

  const [view, setView] = useState<"formFields" | "rawEntry">("formFields");

  return (
    <FormProvider {...methods}>
      <div className={styles.tabs}>
        <button
          className={styles.tab}
          data-selected={view === "formFields"}
          onClick={() => setView("formFields")}
        >
          Form fields
        </button>
        <button
          className={styles.tab}
          data-selected={view === "rawEntry"}
          onClick={() => setView("rawEntry")}
        >
          Raw entry
        </button>
      </div>

      {view === "formFields" && (
        <form
          onSubmit={methods.handleSubmit((data) =>
            onSubmit({ ...customisation, ...data })
          )}
        >
          <div className={styles.fields}>
            <div className={styles.schemaForm}>
              <ErrorBoundary>
                <SliceZone
                  components={components}
                  slices={schemaForm.data.slices}
                />
              </ErrorBoundary>
            </div>
          </div>

          <div className={styles.cta}>
            <PrimaryButton type="submit">Submit</PrimaryButton>
          </div>
        </form>
      )}

      {view === "rawEntry" && (
        <form
          onSubmit={methods.handleSubmit((data) =>
            onSubmit(JSON.parse(data.raw))
          )}
        >
          <div className={styles.fields}>
            <MultiLineText
              defaultValue={JSON.stringify(customisation, undefined, 2)}
              {...methods.register("raw")}
            />
          </div>

          <div className={styles.cta}>
            <PrimaryButton type="submit">Submit</PrimaryButton>
          </div>
        </form>
      )}
    </FormProvider>
  );
}
